import {Badge, Popover} from 'antd'
import classNames from 'classnames'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext, useLayoutEffect, useRef, useState} from 'react'
import {FiMoreHorizontal} from 'react-icons/fi'
import {LoginContext} from '../../../components/LoginContext'

const MORE_BTN_WIDTH = 44

export const TabNavigation = ({
                                  tabs = [],
                                  activeTab,
                                  onTabChange,
                                  className,
                                  style,
                              }) => {
    const t = useTranslate()
    const login = useContext(LoginContext)
    const outerRef = useRef(null)
    const [visibleCount, setVisibleCount] = useState(tabs.length)
    const [moreOpen, setMoreOpen] = useState(false)

    useLayoutEffect(() => {
        const outer = outerRef.current
        if (!outer) return

        const compute = () => {
            const tabEls = Array.from(outer.querySelectorAll('[data-tab-index]'))
            if (!tabEls.length) return

            const outerW = outer.clientWidth
            const lastEl = tabEls[tabEls.length - 1]
            const totalRight = lastEl.offsetLeft + lastEl.offsetWidth

            if (totalRight <= outerW) {
                setVisibleCount(tabEls.length)
                return
            }

            const avail = outerW - MORE_BTN_WIDTH
            let count = 0
            for (const el of tabEls) {
                if (el.offsetLeft + el.offsetWidth <= avail) {
                    count++
                } else {
                    break
                }
            }
            setVisibleCount(Math.max(1, count))
        }

        compute()
        const ro = new ResizeObserver(compute)
        ro.observe(outer)
        return () => ro.disconnect()
    }, [tabs])

    // visibleCount (đo bằng ResizeObserver, có thể sai số ở 1 số layout/thời điểm đo) giờ CHỈ còn
    // quyết định danh sách overflowTabs này - tab nào được liệt kê THÊM trong popover "..." làm lối
    // tắt (khỏi cuộn ngang). KHÔNG còn dùng để khoá click ở hàng tab chính nữa (trước đây
    // pointer-events-none/aria-hidden/tabIndex=-1 dựa thẳng vào phép đo này - sai số làm tab vẫn
    // hiện đủ, bấm được bằng mắt thường nhưng bị khoá click, bạn báo lỗi này 2 lần ở 2 màn khác
    // nhau - sửa hẹp theo từng case trước không tổng quát). Hàng tab chính LUÔN tự cuộn ngang được
    // (overflow-x-auto ở div cha) nên tự nó đã là 1 UI điều hướng đầy đủ - dù đo sai, tệ nhất chỉ là
    // popover thiếu/thừa 1 mục, không bao giờ còn chặn được thao tác bấm tab chính nữa.
    const overflowTabs = tabs.slice(visibleCount)
    const hasMore = overflowTabs.length > 0
    const activeInOverflow = overflowTabs.some((tab) => tab.key === activeTab)

    const moreContent = (
        <div className="flex flex-col min-w-[160px]">
            {overflowTabs.map((tab) => (
                <button
                    key={tab.key}
                    onClick={() => {
                        onTabChange(tab.key)
                        setMoreOpen(false)
                    }}
                    className={classNames(
                        'flex items-center gap-2 px-3 py-2.5 text-sm rounded-md transition-colors text-left w-full',
                        activeTab === tab.key
                            ? 'text-indigo-600 bg-indigo-50'
                            : 'text-color-200 hover:text-indigo-500 hover:bg-color-50'
                    )}>
                    {tab?.icon}
                    <span>{t(tab.name)}</span>
                    {(tab?.countValue !== undefined && tab?.countValue !== null) && (
                        <span className="ml-auto px-1.5 py-0.5 rounded-full text-2xs font-semibold leading-none background-200 text-color-300">
                            {tab.countValue}
                        </span>
                    )}
                </button>
            ))}
        </div>
    )

    return (
        <div
            ref={outerRef}
            style={style}
            className={classNames(
                'flex background-100 rounded-t-xl border-b sticky',
                className
            )}>
            <div
                style={{padding: '0.25rem 0.5rem'}}
                className="flex gap-1 overflow-hidden overflow-x-auto scrollBarMedium flex-1">
                {tabs.map((tab, i) => {
                    return (
                        <button
                            key={tab.key}
                            data-tab-index={i}
                            onClick={() => onTabChange(tab.key)}
                            style={{borderRadius: '0.5rem 0.5rem 0.25rem 0.25rem'}}
                            className={classNames(
                                'flex items-center flex-shrink-0 gap-2 px-3 py-2 text-sm font-medium border-b-3 transition-all duration-300',
                                activeTab === tab.key
                                    ? 'background border-indigo-500 text-indigo-600'
                                    : 'background-50 hover:bg-secondary-50 border-transparent text-color-200 hover:text-secondary-500'
                            )}>
                            {tab?.count ? (
                                <Badge
                                    dot
                                    showZero={false}
                                    className="custom-badge-color"
                                    count={_.get(login, tab.count, 0)}>
                                    {tab?.icon}
                                </Badge>
                            ) : (
                                tab?.icon
                            )}
                            <span>{t(tab.name)}</span>
                            {/* countValue - số lượng hiện thẳng cạnh tên tab (vd "Lớp học 7") - KHÁC
                                `count` ở trên (chấm đỏ nhỏ trên icon, tra theo field LoginContext, dùng
                                cho case unread/pending). Optional, không set thì không đổi gì các tab
                                khác đang dùng component này. */}
                            {(tab?.countValue !== undefined && tab?.countValue !== null) && (
                                <span
                                    className={classNames(
                                        'px-1.5 py-0.5 rounded-full text-2xs font-semibold leading-none',
                                        activeTab === tab.key
                                            ? 'bg-indigo-100 text-indigo-600'
                                            : 'background-200 text-color-300'
                                    )}>
                                    {tab.countValue}
                                </span>
                            )}
                        </button>
                    )
                })}
            </div>
            {hasMore && (
                <Popover
                    open={moreOpen}
                    onOpenChange={setMoreOpen}
                    content={moreContent}
                    trigger="click"
                    placement="bottomRight"
                    destroyOnHidden>
                    <button
                        className={classNames(
                            'px-3 flex-shrink-0 flex items-center justify-center self-stretch transition-colors duration-300',
                            activeInOverflow
                                ? 'border-indigo-500 text-indigo-600'
                                : 'border-transparent text-color-300 hover:text-indigo-500 hover:bg-color-50'
                        )}>
                        <FiMoreHorizontal size={18}/>
                    </button>
                </Popover>
            )}
        </div>
    )
}

export default TabNavigation
